import React, { FC, useEffect, useMemo } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'

import { EnergyHistory, InfoPlaceholderSelector, NutritionAnalysisLayout, PanelTextPicker } from '../components'
import useCalculateRange from '../hooks/useCalculateRange'
import useDateRangePanel from '../hooks/useDateRangePanel'

import { DateRangeModal } from 'Components'
import { DateRangeString, EnergyHistoryItem, ReduxStoreState, analyticsEnergyHistoryDateRanges } from 'Models'
import { registrationDateSelector } from 'ReduxStore/coreBackendUser'
import {
  analyticsCacheRevisionSelector,
  energyHistorySelector,
  fetchEnergyHistoryAction,
  isEnergyHistoryLoadingSelector
} from 'ReduxStore/myAnalytics'
import { fetchProgramsAction, programStartDatesSelector } from 'ReduxStore/programs'
import { analyticsEnergyHistoryDateRangeSelector, updateUserSettingsAction } from 'ReduxStore/userSettings'
import { createEnergyHistoryChartData } from 'Utils/helpers/nutritionHelper'

type SelectorProps = {
  analyticsEnergyHistoryDateRange?: DateRangeString
  analyticsCacheRevision: number
  energyHistory: EnergyHistoryItem[]
  programStartDates?: Array<{ value: number }>
  registrationDate: Date
  isEnergyHistoryLoading: boolean
}

const mapStateToProps = createStructuredSelector<ReduxStoreState, SelectorProps>({
  analyticsEnergyHistoryDateRange: analyticsEnergyHistoryDateRangeSelector,
  analyticsCacheRevision: analyticsCacheRevisionSelector,
  energyHistory: energyHistorySelector,
  programStartDates: programStartDatesSelector,
  registrationDate: registrationDateSelector,
  isEnergyHistoryLoading: isEnergyHistoryLoadingSelector
})

const mapDispatchToProps = {
  fetchEnergyHistory: fetchEnergyHistoryAction,
  updateUserSettings: updateUserSettingsAction,
  fetchPrograms: fetchProgramsAction
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

const EnergyHistoryPage: FC<PropsFromRedux> = ({
  fetchEnergyHistory,
  updateUserSettings,
  fetchPrograms,
  analyticsEnergyHistoryDateRange,
  analyticsCacheRevision,
  energyHistory,
  programStartDates,
  registrationDate,
  isEnergyHistoryLoading
}: PropsFromRedux) => {
  const calculateRange = useCalculateRange()

  const energyHistoryPanel = useDateRangePanel({
    analyticsDateRange: analyticsEnergyHistoryDateRange,
    settingsKey: 'analyticsEnergyHistoryDateRange',
    calculateRange,
    fetchFn: fetchEnergyHistory,
    refreshKey: analyticsCacheRevision,
    updateUserSettings
  })

  useEffect(() => {
    fetchPrograms()
  }, [fetchPrograms])

  const averageEnergy = useMemo(() => {
    if (!energyHistory) {
      return NaN
    }

    return Math.round(
      energyHistory.reduce((totalEnergy, day) => totalEnergy + day.acceptedEnergy, 0) / energyHistory.length
    )
  }, [energyHistory])

  return (
    <NutritionAnalysisLayout>
      <PanelTextPicker
        onChange={energyHistoryPanel.handleChange}
        onItemClick={energyHistoryPanel.handleItemClick}
        items={analyticsEnergyHistoryDateRanges}
        selectedKey={analyticsEnergyHistoryDateRange}
        title="Kalorienbilanz"
        additionalInfo={isNaN(averageEnergy) ? undefined : `Durchschnittliche Kalorien pro Tag: ${averageEnergy} kcal`}
        labelOverrides={energyHistoryPanel.labelOverrides}
      >
        {energyHistory.length > 0 || isEnergyHistoryLoading ? (
          <EnergyHistory
            energyHistoryChartData={createEnergyHistoryChartData(energyHistory)}
            programStartDates={programStartDates}
            loading={isEnergyHistoryLoading}
          />
        ) : (
          <InfoPlaceholderSelector dateRange={analyticsEnergyHistoryDateRange} />
        )}
      </PanelTextPicker>

      <DateRangeModal
        isOpen={energyHistoryPanel.showPicker}
        onClose={() => energyHistoryPanel.setShowPicker(false)}
        onApply={energyHistoryPanel.handleApply}
        minimumDate={registrationDate}
      />
    </NutritionAnalysisLayout>
  )
}

export default connector(EnergyHistoryPage)
